import React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { useTrackMutation } from '@mote/api'
import { HamburgerIcon } from '@mote/assets/icons'
import { isIOS, isMobile } from '@mote/helpers/browser'
import {
  ADMIN_ALLOW_LIST,
  CWS_URL,
  IOS_STORE_URL,
  TRACK_EVENT_NAMES
} from '@mote/helpers/constants'
import { useGoalsState } from '@mote/helpers/hooks/business'
import { getAuthAttribute } from '@mote/helpers/identity'
import { Avatar, Block, Dropdown } from '@mote/ui'
import { useAuth } from '../../auth/useAuth'
import { getOpenReplayTracker } from '../../openReplayTracker'
import { Content } from '../shared'

export const DashboardHeader = ({ className, toggleSidebar }) => {
  const history = useHistory()
  const goalsState = useGoalsState()
  const { user, isAuthenticated, logout } = useAuth()
  const emailAddress = React.useMemo(
    () => getAuthAttribute('primaryEmailAddress'),
    []
  )
  const { mutate: track } = useTrackMutation()
  const navbarTestId = React.useMemo(
    () => (isAuthenticated ? 'authenticated-navbar' : 'unauthenticated-navbar'),
    [isAuthenticated]
  )
  const showLastDivider = React.useMemo(
    () =>
      isIOS() ||
      (!user?.isInstitutional && user?.isUpgradable) ||
      (!isMobile() && !goalsState.extensionInstalled),
    [goalsState, user]
  )

  React.useEffect(() => {
    if (!user) return
    const tracker = getOpenReplayTracker()

    tracker?.setUserID(user.primaryEmailAddress)
  }, [user])

  function handleLogout() {
    logout()
  }

  function handleAccountDropdownSelection(key) {
    switch (key) {
      case 'account-settings': {
        history.push('/account')
        break
      }
      case 'admin': {
        history.push('/admin')
        break
      }
      case 'logout': {
        handleLogout()
        break
      }
      case 'download-app': {
        window.open(IOS_STORE_URL, '_new')
        break
      }
      case 'install-extension': {
        track({
          variables: {
            event: TRACK_EVENT_NAMES.extension.clickedInstall,
            referrer_user_id: window.localStorage.getItem(
              'mote_referrer_user_id'
            )
          }
        })
        window.open(CWS_URL, '_new')
        break
      }
      case 'upgrade': {
        history.push('/pricing')
        break
      }
      default:
      // no-op
    }
  }

  return (
    <Root data-testid={navbarTestId} className={className}>
      <Container>
        <StyledContent variant='wide'>
          <SidebarToggler onClick={toggleSidebar}>
            <HamburgerIcon color='#25252D' />
          </SidebarToggler>
          <Block layout='horizontal' align='center' gap={8}>
            <StyledDropdown
              onAction={handleAccountDropdownSelection}
              data-trigger-testid='navbar-dropdown-trigger-button'
              triggerElementContent={
                <Avatar
                  name={user?.name}
                  size={24}
                  fontSize={15}
                  src={user?.avatarUrl}
                />
              }
              placement='bottom-right'
              showChevron={false}
            >
              <DropdownHeader key='email-address'>
                <span>{emailAddress}</span>
              </DropdownHeader>
              <Dropdown.Item
                key='account-settings'
                data-testid='navbar-dropdown-my-account-option'
              >
                <Dropdown.ItemText>Account Settings</Dropdown.ItemText>
              </Dropdown.Item>

              {ADMIN_ALLOW_LIST.includes(user?.primaryEmailAddress) && (
                <Dropdown.Item
                  key='admin'
                  data-testid='navbar-dropdown-admin-option'
                >
                  <Dropdown.ItemText>Admin</Dropdown.ItemText>
                </Dropdown.Item>
              )}

              <Dropdown.Item
                key='logout'
                data-testid='navbar-dropdown-logout-option'
              >
                <Dropdown.ItemText>Sign out</Dropdown.ItemText>
              </Dropdown.Item>

              {showLastDivider && <Dropdown.Divider key='divider' />}

              {!user?.isInstitutional && user?.isUpgradable && (
                <Dropdown.Item key='upgrade' style={{ color: '#ac0ae8' }}>
                  <Dropdown.ItemText>Upgrade Now</Dropdown.ItemText>
                </Dropdown.Item>
              )}

              {isIOS() && (
                <Dropdown.Item key='download-app'>
                  <Dropdown.ItemText>Download the app</Dropdown.ItemText>
                </Dropdown.Item>
              )}
              {!isMobile() && !goalsState.extensionInstalled && (
                <Dropdown.Item key='install-extension'>
                  <Dropdown.ItemText>Install the extension</Dropdown.ItemText>
                </Dropdown.Item>
              )}
            </StyledDropdown>
          </Block>
        </StyledContent>
      </Container>
    </Root>
  )
}

const Root = styled.header`
  position: sticky;
  height: 88px;
  top: 0;
  z-index: 20;
  margin: 0 -40px;
  padding: 0 40px;
  background-color: #ffffff;

  @media (max-width: 920px) {
    margin: 0 -16px;
    padding: 0 16px;
  }
`

const Container = styled.div`
  padding: 0 8px;
  height: 100%;
`

const StyledContent = styled(Content)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 !important;
  height: 100%;
`

const SidebarToggler = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #f6f6f9;
  }
`

const StyledDropdown = styled(Dropdown)`
  .dropdown-trigger-container {
    height: 40px;
    width: 40px;
  }
`

const DropdownHeader = styled(Dropdown.Header)`
  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`
